import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { tasksBoardApi } from '../../api/tasksBoardApi';
import { type CommentWithTaskInfo } from '../../api/tasksBoardTypes';

export type CommentsFilter = 'ALL' | 'READ' | 'UNREAD';

type NewComment = CommentWithTaskInfo & { viewedAt: number | null };

interface NewCommentsState {
  comments: NewComment[];
  filter: CommentsFilter;
  isLoading: boolean;
}

const initialState: NewCommentsState = {
  comments: [],
  filter: 'ALL',
  isLoading: false,
};

export const newCommentsSlice = createSlice({
  name: 'newComments',
  initialState,
  reducers: {
    addNewComment: (state, action: PayloadAction<CommentWithTaskInfo>) => {
      state.comments = [{ ...action.payload, isViewed: false, viewedAt: null }, ...state.comments];
    },
    markAsViewed(state, action: PayloadAction<{ ids: number[] }>) {
      const idsSet = new Set(action.payload.ids);

      for (const comment of state.comments) {
        if (idsSet.has(comment.id) && !comment.isViewed) {
          comment.isViewed = true;
          comment.viewedAt = Date.now();
        }
      }
    },
    setFilter(state, action: PayloadAction<CommentsFilter>) {
      state.filter = action.payload;
    },
    clearReadComments(state) {
      state.comments = state.comments.filter((comment) => !comment.isViewed);
    },
    updateNewComment(
      state,
      action: PayloadAction<{
        id: number;
        patch: Partial<Omit<NewComment, 'task' | 'id'>>;
      }>
    ) {
      const { id, patch } = action.payload;

      const comment = state.comments.find((comment) => comment.id === id);
      if (!comment) return;

      Object.assign(comment, patch);
    },
    deleteNewComment(state, action: PayloadAction<{ id: number }>) {
      const { id } = action.payload;

      state.comments = state.comments.filter((comment) => comment.id !== id);
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(tasksBoardApi.endpoints.fetchNewComments.matchPending, (state) => {
        state.isLoading = true;
      })
      .addMatcher(tasksBoardApi.endpoints.fetchNewComments.matchFulfilled, (state, action) => {
        state.isLoading = false;

        state.comments = action.payload.data.map((comment) => ({ ...comment, viewedAt: null }));
      })
      .addMatcher(tasksBoardApi.endpoints.fetchNewComments.matchRejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const {
  addNewComment,
  markAsViewed,
  setFilter,
  clearReadComments,
  updateNewComment,
  deleteNewComment,
} = newCommentsSlice.actions;
export const newCommentsReducer = newCommentsSlice.reducer;
